import { memo, useMemo } from "react";
import moment, { Moment } from "moment";
import { makeStyles } from "tss-react/mui";
import { MiIcon } from "@miplatform/components";
import { ICalendarWorkTemplateBase } from "@miplatform/types";
import { isSameTime } from "@miplatform/utils";
import { mdiDotsVertical, mdiProgressClock } from "@mdi/js";
import { Avatar, Box, Typography } from "@mui/material";
import { ICalendarWorkCapacityConfig } from "../../ConfigCalendarSetList.types";

const HOUR_HEIGHT = 52;

export const CalendarWorkCapacityBoardPreview = memo(
  ({
    startTime,
    endTime,
    workCapacities,
    workTemplateBases,
    durations,
    userName = "Assignable User",
  }: BoardPreviewProps) => {
    const { classes, theme } = useStyles();
    const workTemplateBasesMap = useMemo(() => {
      const map: Record<string, ICalendarWorkTemplateBase> = {};
      workTemplateBases.forEach((template) => {
        map[template.id] = template;
      });
      return map;
    }, [workTemplateBases]);

    const timeLabels = useMemo(() => {
      const labels = [];
      const current = moment(startTime).startOf("hour");
      while (current.isBefore(endTime) && !isSameTime(current, endTime)) {
        labels.push(current.format("h a"));
        current.add(1, "hour");
      }
      return labels;
    }, [startTime, endTime]);

    const sortedCapacities = useMemo(
      () => [...workCapacities].sort((a, b) => a.displayOrder - b.displayOrder),
      [workCapacities]
    );

    return (
      <Box className={classes.container}>
        <Box className={classes.userHeader}>
          <Box className={classes.userHeaderContent}>
            <Box display="flex" justifyContent="space-between">
              <Box display="flex" alignItems="center" gap={1}>
                <Avatar
                  src="/path-to-avatar.jpg"
                  sx={{ width: 21.54, height: 13, borderRadius: "100px" }}
                />
                <Typography noWrap className={classes.userText}>
                  {userName}
                </Typography>
              </Box>
              <MiIcon
                path={mdiDotsVertical}
                size={0.65}
                color={theme.palette.grey.medium}
              />
            </Box>

            <Box display="flex" justifyContent="space-between" mt={0.5}>
              <Box display="flex" alignItems="center" gap={2}>
                <MiIcon
                  path={mdiProgressClock}
                  size={0.65}
                  color={theme.palette.orange.main}
                />
                <Typography variant="caption">On Site</Typography>
              </Box>
              <Typography variant="caption">0:00</Typography>
            </Box>
          </Box>

          <Box height={3} width="100%" bgcolor={theme.palette.common.black} />
        </Box>

        {timeLabels.length > 0 && (
          <Box className={classes.timelineWrapper}>
            <Box>
              {timeLabels.map((label) => (
                <Box
                  key={label}
                  height={`${HOUR_HEIGHT}px`}
                  className={classes.timeLabelBox}
                >
                  <Typography variant="caption" className={classes.timeText}>
                    {label.split(" ")[0]} <br /> {label.split(" ")[1]}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box
              className={classes.contentArea}
              height={timeLabels.length * HOUR_HEIGHT}
            >
              {!sortedCapacities.some(({ amount }) => amount !== 0) ? (
                <Box className={classes.emptyCapacityBlock}>
                  <Typography className={classes.emptyCapacityText}>
                    Add capacity on your right to see your technicians capacity
                  </Typography>
                </Box>
              ) : (
                sortedCapacities.map((item) =>
                  Array.from({
                    length: Math.ceil(item.amount),
                  }).map((_, index) => {
                    const duration = durations[item.orderTemplateId] || 1;

                    return (
                      <Box
                        key={`${
                          workTemplateBasesMap[item.orderTemplateId].name
                        }-${index}`}
                        sx={{
                          boxSizing: "border-box",
                          position: "relative",
                          borderRadius: theme.spacing(0.5),
                          minHeight: (HOUR_HEIGHT - 4.25) * duration,
                          height: (HOUR_HEIGHT - 4.25) * duration,
                          backgroundColor: `${
                            workTemplateBasesMap[item.orderTemplateId].color
                          }4D`,
                          border: `2px solid ${
                            workTemplateBasesMap[item.orderTemplateId].color
                          }99`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginTop: theme.spacing(0.5 * duration),
                          marginBottom: theme.spacing(0.5 * duration),
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: theme.fontSize.small,
                            fontWeight: theme.fontWeight.semiBold,
                            color: `${
                              workTemplateBasesMap[item.orderTemplateId].color
                            }99`,
                          }}
                        >
                          {workTemplateBasesMap[item.orderTemplateId].shortName}
                        </Typography>
                      </Box>
                    );
                  })
                )
              )}
            </Box>
          </Box>
        )}
      </Box>
    );
  }
);

interface BoardPreviewProps {
  startTime: string | Moment;
  endTime: string | Moment;
  workCapacities: ICalendarWorkCapacityConfig[];
  workTemplateBases: ICalendarWorkTemplateBase[];
  durations: Record<string, number>;
  userName?: string;
}

const useStyles = makeStyles()((theme) => ({
  container: {
    width: 234,
    overflow: "hidden",
  },
  userHeader: {
    border: `3px solid ${theme.palette.blue.light}`,
    borderTopLeftRadius: theme.spacing(2),
    borderTopRightRadius: theme.spacing(2),
    borderBottom: "none",
  },
  userHeaderContent: {
    padding: theme.spacing(1),
    paddingBottom: 0,
  },
  userText: {
    fontWeight: theme.fontWeight.semiBold,
    fontSize: theme.typography.pxToRem(10.5),
    textOverflow: "ellipsis",
    maxWidth: 170,
    whiteSpace: "nowrap",
  },
  timelineWrapper: {
    display: "flex",
  },
  timeLabelBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 24,
    minHeight: HOUR_HEIGHT,
    maxHeight: HOUR_HEIGHT,
    boxSizing: "border-box",
    borderBottom: `1px solid ${theme.palette.grey.darkAccent}`,
  },
  timeText: {
    fontSize: theme.typography.pxToRem(10),
    lineHeight: "1.1 !important",
    textAlign: "center",
    color: "#666",
  },
  contentArea: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(0.5, 1),
    overflow: "hidden",
    backgroundColor: theme.palette.blue.light,
  },
  emptyCapacityBlock: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
    backgroundColor: theme.palette.primary.light,
  },
  emptyCapacityText: {
    color: theme.palette.primary.dark,
    fontSize: theme.typography.pxToRem(14),
    maxWidth: 166,
    textAlign: "center",
  },
  workBlock: {
    width: "100%",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
  },
  blockLabel: {
    fontWeight: "bold",
    fontSize: "0.85rem",
  },
}));
